import { useToast } from '@ais-ui/ui-core';
import { useForm } from 'react-hook-form';
import {
  convertPhysicalAtonValuesToBackend,
  mutatePhysicalAtonFormSchema,
  MutatePhysicalAtonFormValues,
} from './atonForm';
import { zodResolver } from '@hookform/resolvers/zod';
import { mutateCallbacks } from '../../utils/mutateCallbacks';
import { useLocalization } from '@ais-ui/locale-management';
import { AtoNWithDate, useAddAton, useUpdateAton } from '@ais-ui/api';
import { useAISMapContext } from '../../Map/AISMapContext';
import { AtonTargetInfo, TargetInfoType } from '@ais-ui/websockets';
import { Dispatch, SetStateAction } from 'react';
import { AtoN } from '@ais-ui/api-spec/config';

export const usePhysicalAtonMutation = (
  setIsSubmitting: Dispatch<SetStateAction<boolean>>,
  setShowDialog: Dispatch<SetStateAction<boolean>>,
  aton: AtoNWithDate | null,
  setAton: Dispatch<SetStateAction<AtoNWithDate | null>>
) => {
  const { toast } = useToast();

  const {
    setShowChooseTargetPanel,
    onChooseTarget,
    onRejectTarget,
    setChooseTargetTypes,
  } = useAISMapContext();

  const { messages } = useLocalization();
  const { mutate: createAton } = useAddAton();
  const { mutate: updateAton } = useUpdateAton();
  const physicalForm = useForm<MutatePhysicalAtonFormValues>({
    resolver: zodResolver(mutatePhysicalAtonFormSchema),
    defaultValues: {
      name: '',
      description: '',
      warningTimeout: '00:07:00',
      failureTimeout: '00:13:00',
      alarms: [],
      selectedGroups: [],
    },
  });

  const onCreatePhysical = async (data: MutatePhysicalAtonFormValues) => {
    const backendValues = convertPhysicalAtonValuesToBackend(data);

    setIsSubmitting(true);

    const callBacks = mutateCallbacks(
      toast,
      messages,
      'create',
      'aton',
      setIsSubmitting,
      setShowDialog
    );

    createAton(backendValues as AtoN, callBacks);
    physicalForm.reset();
  };

  const onUpdatePhysical = async (data: MutatePhysicalAtonFormValues) => {
    if (!aton) {
      toast({
        title: messages.genericToastTitleError,
        description: "Couldn't find AtoN to update",
      });
      return;
    }

    const backendValues = convertPhysicalAtonValuesToBackend(data);

    setIsSubmitting(true);

    const callBacks = mutateCallbacks(
      toast,
      messages,
      'edit',
      'aton',
      setIsSubmitting,
      setShowDialog
    );

    updateAton({ id: aton.uuid as string, aton: backendValues }, callBacks);
    setAton(null);
  };

  const onSelectAtonOnMap = () => {
    setShowDialog(false);
    setShowChooseTargetPanel(true);
    setChooseTargetTypes(['aton']);

    onChooseTarget.current = (target: TargetInfoType) => {
      const aton = target as AtonTargetInfo;
      physicalForm.setValue('name', aton.name);
      physicalForm.setValue('mmsi', parseInt(aton.mmsi));
      physicalForm.setValue('latitude', aton.latitude);
      physicalForm.setValue('longitude', aton.longitude);

      setShowChooseTargetPanel(false);
      setShowDialog(true);
    };

    onRejectTarget.current = () => {
      setShowDialog(true);
      setShowChooseTargetPanel(false);
    };
  };

  return {
    physicalForm,
    onCreatePhysical,
    onUpdatePhysical,
    onSelectAtonOnMap,
  };
};
